import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { RoutePaths } from "../../../../enumStrings";
import {
  useCreateCmpEventMutation,
  useCreateCmpEventTypeMutation,
  useGetChamberMemberProSettingsQuery,
  useGetCmpEventTypesQuery,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { commonStyles } from "../../../common/commonStyles";
import EventWizard from "./wizard/EventWizard";
import { WizardAction } from "./wizard/wizardShared";
import { ResultModal } from "../EventSales/eventSalesShared";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";
import {
  buildBlankEventFormState,
  eventFormStateToInput,
  toEventTypeOptions,
} from "../eventBackendMappers";
import {
  EventFormState,
  EventTypeOption,
  createPixelQaEventFormState,
} from "../eventMockData";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";

const eventTypeColors = [
  commonStyles.cpaGold,
  "#2563EB",
  "#16A34A",
  "#9333EA",
  "#DC2626",
  "#0891B2",
];
const defaultEventTypeLabel = "Other";

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";

const CreateEvent = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // July 9, 2026 direction: creating an event shows the centered success
  // popup (same card as Create Member's success dialog) and every close path
  // lands on Manage Events instead of the old silent jump to Update Event.
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const pixelQaEnabled =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(location.search).get("pixelQa") === "1";
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "create-step-1"
  );

  const { data: eventTypesData, refetch: refetchEventTypes } =
    useGetCmpEventTypesQuery({
      variables: { input: { chamberId, includeArchived: false } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const [createCmpEventType] = useCreateCmpEventTypeMutation();
  const [createCmpEvent, { loading: creatingEvent }] =
    useCreateCmpEventMutation();

  const eventTypes = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData?.getCmpEventTypes.eventTypes]
  );
  const initialEvent = useMemo(
    () => {
      if (pixelQaEnabled) {
        const qaEventType =
          eventTypes.find((eventType) => eventType.label === "Awards Gala") ||
          eventTypes[0];
        return createPixelQaEventFormState(qaEventType);
      }

      return buildBlankEventFormState(eventTypes);
    },
    [eventTypes, pixelQaEnabled]
  );
  const timeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone || getBrowserTimeZone()
  ).timeZone;

  const addEventType = async (label: string): Promise<EventTypeOption | null> => {
    const result = await createCmpEventType({
      variables: {
        input: {
          chamberId,
          label,
          color: eventTypeColors[eventTypes.length % eventTypeColors.length],
          sortOrder: eventTypes.length,
        },
      },
    });
    const createdType = result.data?.createCmpEventType.eventType;
    await refetchEventTypes();
    return createdType
      ? {
          id: createdType.id,
          label: createdType.label,
          color: createdType.color,
          isArchived: createdType.isArchived,
        }
      : null;
  };

  if (pixelQaState && !pixelQaEnabled) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  const getEventWithActiveEventType = async (
    event: EventFormState
  ): Promise<EventFormState> => {
    const latest = await refetchEventTypes();
    const latestEventTypes = toEventTypeOptions(
      latest.data?.getCmpEventTypes.eventTypes || []
    );
    const selected = latestEventTypes.find(
      (eventType) => eventType.id === event.eventTypeId
    );
    if (selected) {
      return { ...event, eventType: selected.label, eventTypeColor: selected.color };
    }
    if (!event.eventTypeId && latestEventTypes[0]) {
      return {
        ...event,
        eventTypeId: latestEventTypes[0].id,
        eventType: latestEventTypes[0].label,
        eventTypeColor: latestEventTypes[0].color,
      };
    }
    if (latestEventTypes.length === 0) {
      const defaultEventType = await addEventType(defaultEventTypeLabel);
      if (!defaultEventType) {
        throw new Error("Create an event type before saving this event.");
      }
      return {
        ...event,
        eventTypeId: defaultEventType.id,
        eventType: defaultEventType.label,
        eventTypeColor: defaultEventType.color,
      };
    }
    throw new Error("Choose an active event type before saving.");
  };

  const handleSubmit = async ({
    event,
    action,
  }: {
    event: EventFormState;
    action: WizardAction;
  }) => {
    setErrorMessage(null);
    try {
      if (!chamberId) {
        throw new Error("Chamber is still loading. Try again in a moment.");
      }
      const statusEvent: EventFormState = {
        ...event,
        status: action === "publish" ? "Published" : "Draft",
      };
      const eventWithActiveEventType =
        await getEventWithActiveEventType(statusEvent);
      const result = await createCmpEvent({
        variables: {
          input: {
            chamberId,
            data: eventFormStateToInput({
              event: eventWithActiveEventType,
              timeZone,
            }),
          },
        },
      });
      const savedEvent = result.data?.createCmpEvent.event;
      if (!savedEvent?.id) {
        throw new Error("The event could not be saved.");
      }
      setSuccessMessage(
        action === "publish"
          ? "Your event has been published and is now live on your website."
          : "Your event has been saved as a draft. You can publish it anytime from Manage Events."
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "The event could not be saved."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessMessage(null);
    navigate(RoutePaths.chamberAdmin_events_manage);
  };

  return (
    <>
      {/* Success popup styled after the Create Member success card
          (MEM-CREATE-020 donor): white rounded card, red circular close,
          green check circle, centered bold title + message. Every close path
          goes through handleCloseSuccessDialog, which redirects to Manage
          Events (July 9, 2026 direction). */}
      <Dialog
        open={Boolean(successMessage)}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <IconButton
          aria-label="Close event created confirmation"
          onClick={handleCloseSuccessDialog}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            backgroundColor: "#EF1B2D",
            color: "white",
            height: 30,
            width: 30,
            "&:hover": { backgroundColor: "#D71920" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <DialogTitle className="text-center">
          <div className="mx-auto mb-4 mt-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-600 text-white">
            <CheckCircleOutlineIcon sx={{ fontSize: 54 }} />
          </div>
          <span className="block text-xl font-bold text-black">
            Event Created Successfully
          </span>
        </DialogTitle>
        <DialogContent>
          <p className="text-center text-sm leading-6 text-black">
            {successMessage}
          </p>
        </DialogContent>
        <DialogActions className="justify-center px-6 pb-6">
          <Button
            type="button"
            variant="contained"
            onClick={handleCloseSuccessDialog}
            sx={{
              backgroundColor: commonStyles.cpaGold,
              color: "white",
              fontWeight: 800,
              minWidth: 140,
              "&:hover": { backgroundColor: commonStyles.cpaGoldDark },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save/publish failures surface as the shared popup instead of
          crashing or an inline banner (EVT-PUBLISH-001). */}
      <ResultModal
        open={Boolean(errorMessage)}
        success={false}
        title="The event could not be saved."
        message={
          errorMessage && errorMessage !== "The event could not be saved."
            ? errorMessage
            : undefined
        }
        onClose={() => setErrorMessage(null)}
      />
      <EventWizard
        mode="create"
        initialEvent={initialEvent}
        eventTypes={eventTypes}
        chamberId={chamberId}
        timeZone={timeZone}
        // Treat the open success dialog as still-saving so the wizard's
        // UnsavedChangesPrompt stays disarmed between save and the redirect —
        // otherwise it re-arms behind the dialog and the post-success
        // navigation trips a false "unsaved changes" warning.
        saving={creatingEvent || Boolean(successMessage)}
        allowStepJumpWithoutValidation={pixelQaEnabled}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default CreateEvent;
